import { useEffect, useRef, useState } from 'preact/hooks';
import { useOddball } from '../midi-context';
import { getAudioContext, createReverbBuffer, createNoiseBuffer } from '../audio/audio-engine';
import { useCCValues } from '../hooks/use-oddball-events';
import type { GestureEvent, RollingEvent, TapEvent, ShakeEvent, TwistEvent, CCUpdateEvent } from '../../src/types';
import { OddballCC } from '../../src/types';

type Environment = 'forest' | 'ocean' | 'storm' | 'night';
const ENV_ORDER: Environment[] = ['forest', 'ocean', 'storm', 'night'];
const ENV_LABELS: Record<Environment, string> = {
  forest: 'Forest',
  ocean: 'Ocean',
  storm: 'Storm',
  night: 'Night',
};

// Melodic scales per environment (pentatonic-ish, themed)
const ENV_SCALES: Record<Environment, number[]> = {
  forest: [392.00, 440.00, 493.88, 587.33, 659.25, 783.99], // G major pent, bright
  ocean:  [293.66, 349.23, 392.00, 440.00, 523.25, 587.33],  // D mixolydian feel, rolling
  storm:  [233.08, 261.63, 311.13, 349.23, 415.30, 466.16],  // Bb minor, dark
  night:  [261.63, 293.66, 329.63, 392.00, 440.00, 523.25],  // C major pent, gentle
};

function mapRange(value: number, inMin: number, inMax: number, outMin: number, outMax: number): number {
  return outMin + ((value - inMin) / (inMax - inMin)) * (outMax - outMin);
}

interface SoundscapeEngine {
  ctx: AudioContext;
  masterGain: GainNode;
  reverb: ConvolverNode;
  wetGain: GainNode;
  // Ambient bed
  noiseSource: AudioBufferSourceNode | null;
  noiseGain: GainNode;
  noiseFilter: BiquadFilterNode;
  noiseLfo: OscillatorNode;
  noiseLfoGain: GainNode;
  // Melodic layer
  melodyOsc: OscillatorNode;
  melodyGain: GainNode;
  melodyFilter: BiquadFilterNode;
  // Additional layers
  layers: Array<{ source: AudioBufferSourceNode; gain: GainNode; filter: BiquadFilterNode }>;
  // Night crickets
  cricketOsc: OscillatorNode | null;
  cricketGain: GainNode | null;
  // Noise buffer for events
  noiseBuffer: AudioBuffer;
  octaveShift: number;
}

function createSoundscapeEngine(): SoundscapeEngine {
  const ctx = getAudioContext();
  const masterGain = ctx.createGain();
  masterGain.gain.value = 0.3;

  const reverb = ctx.createConvolver();
  reverb.buffer = createReverbBuffer(ctx, 3, 2);
  const wetGain = ctx.createGain();
  wetGain.gain.value = 0.4;

  const dryGain = ctx.createGain();
  dryGain.gain.value = 0.6;

  masterGain.connect(dryGain).connect(ctx.destination);
  masterGain.connect(reverb).connect(wetGain).connect(ctx.destination);

  // Ambient noise bed
  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.value = 400;
  noiseFilter.Q.value = 0.8;

  const noiseGain = ctx.createGain();
  noiseGain.gain.value = 0.15;

  // LFO for filter modulation
  const noiseLfo = ctx.createOscillator();
  noiseLfo.type = 'sine';
  noiseLfo.frequency.value = 0.3;
  const noiseLfoGain = ctx.createGain();
  noiseLfoGain.gain.value = 200;
  noiseLfo.connect(noiseLfoGain).connect(noiseFilter.frequency);
  noiseLfo.start();

  noiseFilter.connect(noiseGain).connect(masterGain);

  // Melodic layer — normally silent, twist activates it
  const melodyFilter = ctx.createBiquadFilter();
  melodyFilter.type = 'lowpass';
  melodyFilter.frequency.value = 2500;
  melodyFilter.Q.value = 1;

  const melodyOsc = ctx.createOscillator();
  melodyOsc.type = 'sine';
  melodyOsc.frequency.value = 392;
  const melodyGain = ctx.createGain();
  melodyGain.gain.value = 0;
  melodyOsc.connect(melodyFilter).connect(melodyGain).connect(masterGain);
  melodyOsc.start();

  const noiseBuffer = createNoiseBuffer(ctx, 2);

  return {
    ctx, masterGain, reverb, wetGain,
    noiseSource: null, noiseGain, noiseFilter, noiseLfo, noiseLfoGain,
    melodyOsc, melodyGain, melodyFilter,
    layers: [], cricketOsc: null, cricketGain: null, noiseBuffer,
    octaveShift: 0,
  };
}

function startAmbientBed(eng: SoundscapeEngine) {
  stopAmbientBed(eng);
  const source = eng.ctx.createBufferSource();
  source.buffer = eng.noiseBuffer;
  source.loop = true;
  source.connect(eng.noiseFilter);
  source.start();
  eng.noiseSource = source;
}

function stopAmbientBed(eng: SoundscapeEngine) {
  if (eng.noiseSource) {
    try { eng.noiseSource.stop(); } catch {}
    eng.noiseSource = null;
  }
}

function stopCrickets(eng: SoundscapeEngine) {
  if (eng.cricketOsc) {
    try { eng.cricketOsc.stop(); } catch {}
    eng.cricketOsc = null;
  }
  eng.cricketGain = null;
}

function applyEnvironment(eng: SoundscapeEngine, env: Environment, intensity: number, time: number) {
  const t = eng.ctx.currentTime;
  const timeShift = time * 400;

  switch (env) {
    case 'forest': {
      eng.noiseFilter.type = 'lowpass';
      eng.noiseFilter.frequency.setTargetAtTime(300 + timeShift, t, 0.3);
      eng.noiseFilter.Q.setTargetAtTime(0.5, t, 0.1);
      eng.noiseLfo.frequency.setTargetAtTime(0.2, t, 0.2);
      eng.noiseLfoGain.gain.setTargetAtTime(100, t, 0.2);
      eng.noiseGain.gain.setTargetAtTime(0.1 + intensity * 0.15, t, 0.2);
      stopCrickets(eng);
      break;
    }
    case 'ocean': {
      eng.noiseFilter.type = 'bandpass';
      eng.noiseFilter.frequency.setTargetAtTime(400 + timeShift, t, 0.3);
      eng.noiseFilter.Q.setTargetAtTime(0.6, t, 0.1);
      eng.noiseLfo.frequency.setTargetAtTime(0.15, t, 0.2);
      eng.noiseLfoGain.gain.setTargetAtTime(300, t, 0.2);
      eng.noiseGain.gain.setTargetAtTime(0.12 + intensity * 0.18, t, 0.2);
      stopCrickets(eng);
      break;
    }
    case 'storm': {
      eng.noiseFilter.type = 'bandpass';
      eng.noiseFilter.frequency.setTargetAtTime(600 + timeShift, t, 0.3);
      eng.noiseFilter.Q.setTargetAtTime(2, t, 0.1);
      eng.noiseLfo.frequency.setTargetAtTime(0.5, t, 0.2);
      eng.noiseLfoGain.gain.setTargetAtTime(500, t, 0.2);
      eng.noiseGain.gain.setTargetAtTime(0.15 + intensity * 0.25, t, 0.2);
      stopCrickets(eng);
      break;
    }
    case 'night': {
      // Quiet low-frequency rumble — like distant wind through trees
      eng.noiseFilter.type = 'lowpass';
      eng.noiseFilter.frequency.setTargetAtTime(180 + timeShift * 0.3, t, 0.3);
      eng.noiseFilter.Q.setTargetAtTime(0.4, t, 0.1);
      // Slow amplitude breathing — gentle interruptions
      eng.noiseLfo.frequency.setTargetAtTime(0.07 + intensity * 0.05, t, 0.2);
      eng.noiseLfoGain.gain.setTargetAtTime(30, t, 0.2);
      eng.noiseGain.gain.setTargetAtTime(0.06 + intensity * 0.04, t, 0.2);
      stopCrickets(eng);
      break;
    }
  }
}

function playTapEvent(eng: SoundscapeEngine, env: Environment) {
  const { ctx, masterGain, reverb, wetGain } = eng;
  const t = ctx.currentTime;

  switch (env) {
    case 'forest': {
      // Bird chirp — two-tone warble with natural overtones
      const baseFreq = 2200 + Math.random() * 800; // random pitch variety
      for (let i = 0; i < 2; i++) {
        const chirpT = t + i * 0.09;
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        // Quick pitch swoop up then down
        osc.frequency.setValueAtTime(baseFreq * 0.7, chirpT);
        osc.frequency.linearRampToValueAtTime(baseFreq, chirpT + 0.03);
        osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.85, chirpT + 0.07);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0, chirpT);
        g.gain.linearRampToValueAtTime(0.12, chirpT + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, chirpT + 0.08);
        osc.connect(g).connect(masterGain);
        osc.start(chirpT);
        osc.stop(chirpT + 0.1);
        // Add a quiet harmonic for richness
        const h = ctx.createOscillator();
        h.type = 'sine';
        h.frequency.setValueAtTime(baseFreq * 1.5, chirpT);
        h.frequency.linearRampToValueAtTime(baseFreq * 2, chirpT + 0.03);
        h.frequency.exponentialRampToValueAtTime(baseFreq * 1.7, chirpT + 0.06);
        const hg = ctx.createGain();
        hg.gain.setValueAtTime(0, chirpT);
        hg.gain.linearRampToValueAtTime(0.04, chirpT + 0.005);
        hg.gain.exponentialRampToValueAtTime(0.001, chirpT + 0.06);
        h.connect(hg).connect(masterGain);
        h.start(chirpT);
        h.stop(chirpT + 0.08);
      }
      break;
    }
    case 'ocean': {
      // Splash — noise burst sweeping from high to low, like water hitting
      const noise = ctx.createBufferSource();
      noise.buffer = eng.noiseBuffer;
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.setValueAtTime(3000, t);
      f.frequency.exponentialRampToValueAtTime(400, t + 0.3);
      f.Q.value = 0.8;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.2, t + 0.01);
      g.gain.linearRampToValueAtTime(0.15, t + 0.05);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
      noise.connect(f).connect(g).connect(masterGain);
      noise.start(t);
      noise.stop(t + 0.45);
      // Low body thump — wave impact
      const body = ctx.createOscillator();
      body.type = 'sine';
      body.frequency.setValueAtTime(120, t);
      body.frequency.exponentialRampToValueAtTime(60, t + 0.15);
      const bg = ctx.createGain();
      bg.gain.setValueAtTime(0.1, t);
      bg.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
      body.connect(bg).connect(masterGain);
      body.start(t);
      body.stop(t + 0.25);
      break;
    }
    case 'storm': {
      // Thunder crack — sharp transient then rolling low rumble
      // Crack
      const crack = ctx.createBufferSource();
      crack.buffer = eng.noiseBuffer;
      const cf = ctx.createBiquadFilter();
      cf.type = 'highpass';
      cf.frequency.value = 1500;
      const cg = ctx.createGain();
      cg.gain.setValueAtTime(0.3, t);
      cg.gain.exponentialRampToValueAtTime(0.001, t + 0.06);
      crack.connect(cf).connect(cg).connect(masterGain);
      crack.start(t);
      crack.stop(t + 0.08);
      // Rolling rumble
      const rumble = ctx.createOscillator();
      rumble.type = 'sine';
      rumble.frequency.setValueAtTime(55, t + 0.04);
      rumble.frequency.linearRampToValueAtTime(35, t + 0.8);
      const rg = ctx.createGain();
      rg.gain.setValueAtTime(0, t);
      rg.gain.linearRampToValueAtTime(0.2, t + 0.08);
      rg.gain.exponentialRampToValueAtTime(0.001, t + 0.9);
      rumble.connect(rg).connect(masterGain);
      rumble.start(t);
      rumble.stop(t + 1.0);
      // Noise rumble layer
      const nRumble = ctx.createBufferSource();
      nRumble.buffer = eng.noiseBuffer;
      const nf = ctx.createBiquadFilter();
      nf.type = 'lowpass';
      nf.frequency.setValueAtTime(200, t + 0.04);
      nf.frequency.linearRampToValueAtTime(80, t + 0.7);
      const ng = ctx.createGain();
      ng.gain.setValueAtTime(0, t);
      ng.gain.linearRampToValueAtTime(0.12, t + 0.1);
      ng.gain.exponentialRampToValueAtTime(0.001, t + 0.8);
      nRumble.connect(nf).connect(ng).connect(masterGain);
      nRumble.start(t);
      nRumble.stop(t + 0.9);
      break;
    }
    case 'night': {
      // Echoing footstep — short percussive thump with delayed echoes
      const playStep = (time: number, vol: number) => {
        // Thump body
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(150, time);
        osc.frequency.exponentialRampToValueAtTime(60, time + 0.06);
        const g = ctx.createGain();
        g.gain.setValueAtTime(vol, time);
        g.gain.exponentialRampToValueAtTime(0.001, time + 0.1);
        osc.connect(g).connect(masterGain);
        osc.start(time);
        osc.stop(time + 0.12);
        // High "click" — heel strike
        const click = ctx.createBufferSource();
        click.buffer = eng.noiseBuffer;
        const cf = ctx.createBiquadFilter();
        cf.type = 'bandpass';
        cf.frequency.value = 1800;
        cf.Q.value = 3;
        const cg = ctx.createGain();
        cg.gain.setValueAtTime(vol * 0.6, time);
        cg.gain.exponentialRampToValueAtTime(0.001, time + 0.04);
        click.connect(cf).connect(cg).connect(masterGain);
        click.start(time);
        click.stop(time + 0.05);
      };
      // Primary footstep
      playStep(t, 0.15);
      // Echoes — progressively quieter and delayed
      playStep(t + 0.18, 0.06);
      playStep(t + 0.34, 0.025);
      break;
    }
  }
}

function playShakeEvent(eng: SoundscapeEngine, env: Environment) {
  const { ctx, masterGain } = eng;
  const t = ctx.currentTime;

  switch (env) {
    case 'forest': {
      // Rustling leaves — bandpass noise with quick modulation
      const noise = ctx.createBufferSource();
      noise.buffer = eng.noiseBuffer;
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.setValueAtTime(3000, t);
      f.frequency.linearRampToValueAtTime(5000, t + 0.08);
      f.frequency.linearRampToValueAtTime(2500, t + 0.2);
      f.Q.value = 0.8;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.12, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
      noise.connect(f).connect(g).connect(masterGain);
      noise.start(t);
      noise.stop(t + 0.3);
      break;
    }
    case 'ocean': {
      // Whoosh / wave surge — sweeping noise from low to high
      const noise = ctx.createBufferSource();
      noise.buffer = eng.noiseBuffer;
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.setValueAtTime(200, t);
      f.frequency.linearRampToValueAtTime(2000, t + 0.15);
      f.frequency.exponentialRampToValueAtTime(300, t + 0.4);
      f.Q.value = 0.6;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.18, t + 0.1);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
      noise.connect(f).connect(g).connect(masterGain);
      noise.start(t);
      noise.stop(t + 0.5);
      break;
    }
    case 'storm': {
      // Lightning crack — sharp bright transient
      const noise = ctx.createBufferSource();
      noise.buffer = eng.noiseBuffer;
      const f = ctx.createBiquadFilter();
      f.type = 'highpass';
      f.frequency.value = 3000;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.3, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
      noise.connect(f).connect(g).connect(masterGain);
      noise.start(t);
      noise.stop(t + 0.07);
      break;
    }
    case 'night': {
      // Wind gust — low noise sweep
      const noise = ctx.createBufferSource();
      noise.buffer = eng.noiseBuffer;
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.setValueAtTime(150, t);
      f.frequency.linearRampToValueAtTime(600, t + 0.2);
      f.frequency.exponentialRampToValueAtTime(100, t + 0.6);
      f.Q.value = 0.5;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.1, t + 0.1);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.7);
      noise.connect(f).connect(g).connect(masterGain);
      noise.start(t);
      noise.stop(t + 0.75);
      break;
    }
  }
}

function playMelodyNote(eng: SoundscapeEngine, env: Environment, orientX: number) {
  const scale = ENV_SCALES[env];
  const degree = Math.floor(mapRange(orientX, 0, 127, 0, scale.length - 0.01));
  const freq = scale[Math.max(0, Math.min(degree, scale.length - 1))] * Math.pow(2, eng.octaveShift);
  const t = eng.ctx.currentTime;

  eng.melodyOsc.frequency.setTargetAtTime(freq, t, 0.01);
  eng.melodyGain.gain.cancelScheduledValues(t);
  eng.melodyGain.gain.setValueAtTime(0.18, t);
  eng.melodyGain.gain.setTargetAtTime(0.02, t + 0.05, 0.4);
  eng.melodyGain.gain.setTargetAtTime(0, t + 1.5, 0.3);
}

function addAmbientLayer(eng: SoundscapeEngine) {
  if (eng.layers.length >= 4) return;
  const source = eng.ctx.createBufferSource();
  source.buffer = eng.noiseBuffer;
  source.loop = true;
  const filter = eng.ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 300 + eng.layers.length * 400;
  filter.Q.value = 0.5 + eng.layers.length * 0.3;
  const gain = eng.ctx.createGain();
  gain.gain.value = 0.04;
  source.connect(filter).connect(gain).connect(eng.masterGain);
  source.start();
  eng.layers.push({ source, gain, filter });
}

function clearLayers(eng: SoundscapeEngine) {
  eng.layers.forEach((l) => {
    try { l.source.stop(); } catch {}
  });
  eng.layers = [];
}

export function NatureSoundscape() {
  const { parser, detector, connected, octaveOffset } = useOddball();
  const cc = useCCValues();
  const engineRef = useRef<SoundscapeEngine | null>(null);
  const octaveRef = useRef(0);

  const [environment, setEnvironment] = useState<Environment>('forest');

  useEffect(() => { octaveRef.current = octaveOffset; }, [octaveOffset]);
  useEffect(() => {
    const eng = engineRef.current;
    if (eng) eng.masterGain.gain.setTargetAtTime(connected ? 0.3 : 0, eng.ctx.currentTime, 0.05);
  }, [connected]);
  const [intensity, setIntensity] = useState(0.3);
  const [time, setTime] = useState(0);
  const [layerCount, setLayerCount] = useState(0);

  const envRef = useRef<Environment>('forest');
  const intensityRef = useRef(0.3);
  const timeRef = useRef(0);
  const orientXRef = useRef(64);
  const shakeCountRef = useRef(0);
  const lastShakeTimeRef = useRef(0);

  const switchEnv = (eng: SoundscapeEngine, next: Environment) => {
    envRef.current = next;
    setEnvironment(next);
    clearLayers(eng);
    setLayerCount(0);
    applyEnvironment(eng, next, intensityRef.current, timeRef.current);
  };

  const switchToEnv = (env: Environment) => {
    const eng = engineRef.current;
    if (eng) switchEnv(eng, env);
  };

  useEffect(() => {
    const eng = createSoundscapeEngine();
    engineRef.current = eng;
    startAmbientBed(eng);
    applyEnvironment(eng, 'forest', 0.3, 0);

    // Intensity decay interval
    const decayInterval = window.setInterval(() => {
      if (intensityRef.current > 0.3) {
        intensityRef.current = Math.max(0.3, intensityRef.current - 0.005);
        setIntensity(intensityRef.current);
        if (engineRef.current) {
          applyEnvironment(engineRef.current, envRef.current, intensityRef.current, timeRef.current);
        }
      }
    }, 100);

    // Gesture events (catch, drop, roll, sustained shake)
    const onGesture = (evt: GestureEvent) => {
      switch (evt.type) {
        case 'caught': {
          const idx = ENV_ORDER.indexOf(envRef.current);
          switchEnv(eng, ENV_ORDER[(idx + 1) % ENV_ORDER.length]);
          break;
        }
        case 'landed': {
          const idx = ENV_ORDER.indexOf(envRef.current);
          switchEnv(eng, ENV_ORDER[(idx - 1 + ENV_ORDER.length) % ENV_ORDER.length]);
          break;
        }
        case 'sustainedShake': {
          // Sustained shake advances environment — much easier than catch
          const idx = ENV_ORDER.indexOf(envRef.current);
          switchEnv(eng, ENV_ORDER[(idx + 1) % ENV_ORDER.length]);
          break;
        }
        case 'rollStart': {
          addAmbientLayer(eng);
          setLayerCount(eng.layers.length);
          break;
        }
        case 'rolling': {
          // Modulate ambient bed LFO rate based on oscillation frequency
          const rollingEvt = evt as RollingEvent;
          const baseRate = 0.3;
          const lfoRate = baseRate + rollingEvt.oscillationFreq * 0.5;
          eng.noiseLfo.frequency.setTargetAtTime(lfoRate, eng.ctx.currentTime, 0.1);
          break;
        }
      }
    };

    const onTap = (_evt: TapEvent) => {
      playTapEvent(eng, envRef.current);
    };

    const onShake = (_evt: ShakeEvent) => {
      const now = Date.now();
      // Reset shake count if too much time between shakes
      if (now - lastShakeTimeRef.current > 1500) {
        shakeCountRef.current = 0;
      }
      shakeCountRef.current++;
      lastShakeTimeRef.current = now;

      // 4 quick shakes = advance environment
      if (shakeCountRef.current >= 4) {
        shakeCountRef.current = 0;
        const idx = ENV_ORDER.indexOf(envRef.current);
        switchEnv(eng, ENV_ORDER[(idx + 1) % ENV_ORDER.length]);
      } else {
        intensityRef.current = Math.min(1, intensityRef.current + 0.1);
        setIntensity(intensityRef.current);
        playShakeEvent(eng, envRef.current);
        applyEnvironment(eng, envRef.current, intensityRef.current, timeRef.current);
      }
    };

    const onTwist = (_evt: TwistEvent) => {
      // Advance time
      timeRef.current = (timeRef.current + 0.1) % 1;
      setTime(timeRef.current);
      applyEnvironment(eng, envRef.current, intensityRef.current, timeRef.current);
      // Play melody note
      playMelodyNote(eng, envRef.current, orientXRef.current);
    };

    const onCC = (evt: CCUpdateEvent) => {
      eng.octaveShift = octaveRef.current;
      const t = eng.ctx.currentTime;
      switch (evt.cc) {
        case OddballCC.OrientX: {
          orientXRef.current = evt.value;
          const cutoff = mapRange(evt.value, 0, 127, 150, 2000);
          eng.noiseFilter.frequency.setTargetAtTime(cutoff, t, 0.1);
          break;
        }
        case OddballCC.OrientY: {
          const wet = mapRange(evt.value, 0, 127, 0.1, 0.8);
          eng.wetGain.gain.setTargetAtTime(wet, t, 0.1);
          // Also modulate melody filter for warmth
          const mCutoff = mapRange(evt.value, 0, 127, 800, 4000);
          eng.melodyFilter.frequency.setTargetAtTime(mCutoff, t, 0.1);
          break;
        }
        case OddballCC.SpinSmoothed: {
          const rate = mapRange(evt.value, 0, 127, 0.05, 2);
          eng.noiseLfo.frequency.setTargetAtTime(rate, t, 0.1);
          break;
        }
      }
    };

    detector.on('gesture', onGesture);
    parser.on('tap', onTap);
    parser.on('shake', onShake);
    parser.on('twist', onTwist);
    parser.on('cc', onCC);

    return () => {
      clearInterval(decayInterval);
      detector.off('gesture', onGesture);
      parser.off('tap', onTap);
      parser.off('shake', onShake);
      parser.off('twist', onTwist);
      parser.off('cc', onCC);
      stopAmbientBed(eng);
      stopCrickets(eng);
      clearLayers(eng);
      eng.melodyOsc.stop();
      eng.noiseLfo.stop();
      eng.masterGain.disconnect();
    };
  }, [parser, detector]);

  const envColors: Record<Environment, string> = {
    forest: '#3a5',
    ocean: '#38a',
    storm: '#668',
    night: '#449',
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', paddingTop: '32px',
      minHeight: '100%',
      background: `radial-gradient(ellipse at center, ${envColors[environment]}33, transparent)`,
      borderRadius: '12px', padding: '40px',
    }}>
      <h2 style={{ fontSize: '1rem', fontWeight: 300, color: '#888' }}>Nature Soundscape</h2>

      {/* Environment display */}
      <div style={{ fontSize: '2rem', fontWeight: 200, color: envColors[environment], transition: 'color 0.5s' }}>
        {ENV_LABELS[environment]}
      </div>

      {/* Environment selector buttons */}
      <div style={{ display: 'flex', gap: '6px' }}>
        {ENV_ORDER.map((env) => (
          <button
            key={env}
            onClick={() => switchToEnv(env)}
            style={{
              padding: '5px 12px',
              borderRadius: '12px',
              border: `1px solid ${environment === env ? envColors[env] : '#333'}`,
              background: environment === env ? `${envColors[env]}22` : 'transparent',
              color: environment === env ? envColors[env] : '#666',
              cursor: 'pointer',
              fontSize: '0.7rem',
              fontWeight: environment === env ? 600 : 400,
              transition: 'all 0.3s',
            }}
          >
            {ENV_LABELS[env]}
          </button>
        ))}
      </div>

      {/* Intensity bar */}
      <div style={{ width: '200px' }}>
        <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '4px' }}>Intensity</div>
        <div style={{ width: '100%', height: '6px', background: '#222', borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{
            width: `${intensity * 100}%`,
            height: '100%',
            background: envColors[environment],
            borderRadius: '3px',
            transition: 'width 0.1s',
          }} />
        </div>
      </div>

      {/* Time indicator */}
      <div style={{ width: '200px' }}>
        <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '4px' }}>Time: {Math.round(time * 100)}%</div>
        <div style={{ width: '100%', height: '6px', background: '#222', borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{
            width: `${time * 100}%`,
            height: '100%',
            background: '#777',
            borderRadius: '3px',
            transition: 'width 0.1s',
          }} />
        </div>
      </div>

      {/* Layer count dots */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <span style={{ fontSize: '0.7rem', color: '#666' }}>Layers:</span>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{
            width: '10px', height: '10px', borderRadius: '50%',
            background: i < layerCount ? envColors[environment] : '#333',
            transition: 'background 0.3s',
          }} />
        ))}
      </div>

      <div style={{ fontSize: '0.7rem', color: '#444', maxWidth: '300px', textAlign: 'center', lineHeight: 1.6 }}>
        Tap = event sound &middot; Twist = melody note + shift time
        <br />
        Shake = intensity &middot; Shake x4 = next environment &middot; Roll = add layer
      </div>
    </div>
  );
}
